import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Reservation,
  ReservationStatus,
  StockWarning,
  CreateReservationInput,
  UpdateReservationInput,
  fetchReservationsByOrder,
  createReservation,
  updateReservation,
  deleteReservation,
} from "services/reservations.service";

interface ReservationsState {
  reservations: Reservation[];
  selectedReservation: Reservation | null;
  isLoading: boolean;
  isError: boolean;
  errMsg: string | null;
}

const initialState: ReservationsState = {
  reservations: [],
  selectedReservation: null,
  isLoading: false,
  isError: false,
  errMsg: null,
};

const ReservationsSlice = createSlice({
  name: "reservations",
  initialState,
  reducers: {
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
      state.errMsg = null;
    },
    setReservations(state, action: PayloadAction<Reservation[]>) {
      state.isLoading = false;
      // Filter out any invalid reservations
      state.reservations = (action.payload || []).filter((r) => r && r.id);
    },
    setSelectedReservation(state, action: PayloadAction<Reservation | null>) {
      state.selectedReservation = action.payload;
    },
    addReservation(state, action: PayloadAction<Reservation>) {
      // Only add if reservation has valid id
      if (action.payload && action.payload.id) {
        state.reservations.push(action.payload);
      }
    },
    updateReservationInList(state, action: PayloadAction<Reservation>) {
      const index = state.reservations.findIndex(
        (r) => r.id === action.payload.id
      );
      if (index !== -1) {
        state.reservations[index] = action.payload;
      }
      if (state.selectedReservation?.id === action.payload.id) {
        state.selectedReservation = action.payload;
      }
    },
    removeReservation(state, action: PayloadAction<string>) {
      state.reservations = state.reservations.filter(
        (r) => r.id !== action.payload
      );
      if (state.selectedReservation?.id === action.payload) {
        state.selectedReservation = null;
      }
    },
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload;
    },
    clearError(state) {
      state.isError = false;
      state.errMsg = null;
    },
    resetReservations(state) {
      state.reservations = [];
      state.selectedReservation = null;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = null;
    },
  },
});

export const {
  setLoading,
  setReservations,
  setSelectedReservation,
  addReservation,
  updateReservationInList,
  removeReservation,
  setError,
  clearError,
  resetReservations,
} = ReservationsSlice.actions;

export default ReservationsSlice.reducer;

// Thunks
export const fetchOrderReservations =
  (companyId: string, orderId: string) => async (dispatch: any) => {
    dispatch(setLoading());
    try {
      const response = await fetchReservationsByOrder(companyId, orderId);
      dispatch(setReservations(response?.reservations || []));
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to fetch reservations"));
    }
  };

export const createOrderReservation =
  (
    companyId: string,
    orderId: string,
    data: CreateReservationInput
  ) =>
  async (dispatch: any): Promise<{ reservation: Reservation; warnings?: StockWarning[] }> => {
    try {
      const response = await createReservation(companyId, orderId, data);
      const reservation = response?.reservation;
      if (reservation && reservation.id) {
        dispatch(addReservation(reservation));
      }
      return { reservation, warnings: response?.warnings || [] };
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to create reservation"));
      throw e;
    }
  };

export const updateOrderReservation =
  (
    companyId: string,
    reservationId: string,
    data: UpdateReservationInput
  ) =>
  async (dispatch: any): Promise<{ reservation: Reservation; warnings?: StockWarning[] }> => {
    try {
      const response = await updateReservation(companyId, reservationId, data);
      dispatch(updateReservationInList(response.reservation));
      return { reservation: response.reservation, warnings: response.warnings || [] };
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to update reservation"));
      throw e;
    }
  };

export const deleteOrderReservation =
  (companyId: string, reservationId: string) => async (dispatch: any) => {
    try {
      await deleteReservation(companyId, reservationId);
      dispatch(removeReservation(reservationId));
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to delete reservation"));
      throw e;
    }
  };
